// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getProductStateByPath,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import { parseBoolean } from "@synd/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => {
    // XNG-Changes: Added productState.
    const location = getLocation(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ productPath: string }>(location.search).productPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const productState = getProductStateByPath(state, productPath);
    return {
        productSettings: getSettingsCollection(state).productSettings,
        productState,
        variantChildren: getVariantChildrenDataView(state, productState?.value?.id).value,
    };
};

export const productUnitOfMeasureSelectStyles: SelectPresentationProps = {};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: {
            unitOfMeasures,
            // XNG-Changes:
            properties,
        },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    variantChildren,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));
    const variant = variantChildren?.find(({ id }) => productId === id);
    // <XNG-Changes> Select CS as unit of measure and disable UOM selector if sellAsCase is true and contains CS.
    const sellAsCase = parseBoolean(variant ? variant.properties.sellAsCase : properties?.sellAsCase);
    const CS = "CS";
    const csUom = unitOfMeasures?.find(({ unitOfMeasure }) => unitOfMeasure === CS);

    React.useEffect(() => {
        if (unitOfMeasure === CS || (unitOfMeasures && unitOfMeasures.length <= 1)) {
            return;
        }
        if (sellAsCase && csUom && onUnitOfMeasureChanged) {
            onUnitOfMeasureChanged(csUom.unitOfMeasure);
        }
    }, [unitOfMeasures, sellAsCase, unitOfMeasure, csUom, onUnitOfMeasureChanged]);

    const isDisabled = disabled || (sellAsCase && !!csUom);

    // </XNG-Changes>

    // XNG-Changes
    if (sellAsCase && !!csUom) {
        return null;
    };
    
    if (
        !productSettings?.alternateUnitsOfMeasure ||
        !unitOfMeasures ||
        unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
    ) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    return (
        <Select
            label={labelOverride ?? translate("U/M")}
            value={unitOfMeasure}
            onChange={uomChangeHandler}
            // XNG-Changes: use isDisabled
            disabled={isDisabled}
            data-test-selector="product_unitOfMeasureSelect"
            {...styles}
            {...otherProps}
        >
            {unitOfMeasures.map(uom => (
                <option key={uom.id.toString()} value={uom.unitOfMeasure}>
                    {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                    {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                </option>
            ))}
        </Select>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
